/**
 * Shop Entry — the till.
 *
 * Optimised for the fastest possible path through a real transaction: pick an
 * item, confirm, done. Everything else (customer, staff, channel, branch) has
 * a working default and sits below the fold, because a counter with a queue
 * will not fill in eight fields and a till that demands them gets replaced by
 * a paper book.
 *
 * The sale it records is an ordinary CenoX sale, so it appears in Today's
 * report before the customer has left the counter.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  PackagePlus,
  ShoppingBag,
  UserPlus,
} from 'lucide-react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type CatalogueItem,
  type ShopContext,
  type ShopSaleResult,
} from '@cenox/shared';
import { ApiError, api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { useModules } from '../../lib/modules';
import { money } from '../../lib/format';
import {
  TRANSFER_REFERENCE_HINT,
  needsConfirmation,
  needsTransferReference,
  tillStateLabel,
  tillStateTone,
  transferReferenceReady,
  type TillItem,
} from '../../lib/till';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Spinner,
  cn,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';

interface BasketRow {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: string;
}

/** What the till needs of the item on screen, from the loaded list or a search. */
type TillPick = { id: string; name: string; unitPrice: string | null; onHand: number | null };

const fromSearch = (hit: TillItem): TillPick => ({ id: hit.id, name: hit.name, unitPrice: hit.price, onHand: hit.saleableOnHand });

/** A key the server recognises if this exact checkout is sent twice. */
function newCheckoutKey(): string {
  return `till-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function ShopEntryPage(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const { branchId: workingBranchId } = useWorkingLocation();
  /*
    A pharmacy's shop till sells under the pharmacy till's rules, which the
    server enforces: a catalogue item at its price, no typed amounts, and
    goods left unpaid only as a named customer's debt. The screen says so up
    front rather than letting the cashier find out from a refusal.
  */
  const pharmacy = useModules().active.has('pharmacy');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<TillPick | null>(null);
  /**
   * The basket.
   *
   * Empty for the overwhelmingly common single-item sale, which keeps the
   * original one-tap path exactly as it was. It fills only when somebody
   * scans a second thing, and from then on the whole lot checks out as one
   * transaction with one reference.
   */
  const [basket, setBasket] = useState<BasketRow[]>([]);
  const [scan, setScan] = useState('');
  const checkoutKey = useRef(newCheckoutKey());
  const scanField = useRef<HTMLInputElement>(null);
  const [quantity, setQuantity] = useState(1);
  const [priceOverride, setPriceOverride] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [capturingCustomer, setCapturingCustomer] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'unpaid'>('paid');
  // A pharmacy's "Paid" by card or transfer is the cashier saying they saw it arrive.
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const confirmPayment = pharmacy && paymentStatus === 'paid' && needsConfirmation(paymentMethod);
  const referencePayment = confirmPayment && needsTransferReference(paymentMethod);
  const [result, setResult] = useState<ShopSaleResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const welcome = params.get('welcome') === '1';

  const { data, isPending, error: loadError, refetch } = useQuery({
    queryKey: ['shop-context', business?.businessId],
    queryFn: () => api.get<ShopContext>(path('/shop/context')),
    enabled: Boolean(business),
  });

  // Default to the main branch and the first channel, so the common sale needs
  // no attribution decisions at all.
  useEffect(() => {
    if (!data) return;
    /**
     * The till sells from the location the app is pointed at.
     *
     * The working location wins when the till may sell from it, so somebody who
     * switched to Holy Ghost in the header does not have to switch again here.
     * Otherwise the business default, then whatever single location exists —
     * and if none of those resolve, the field stays empty and checkout says so
     * rather than the API rejecting the sale after the customer has paid.
     */
    if (!branchId) {
      const permitted = data.branches;
      const preferred =
        (workingBranchId && permitted.find((b) => b.id === workingBranchId)?.id) ??
        permitted.find((b) => b.isDefault)?.id ??
        permitted[0]?.id ??
        '';
      setBranchId(preferred);
    }
    // The counter is the POS channel. Taking `channels[0]` took whatever the
    // query happened to return first — "API" for a freshly seeded business —
    // so every till sale was stamped `source: pos` and attributed to the API
    // channel at the same time. The channel breakdown then reported counter
    // trade as machine ingestion, and nothing on screen said otherwise.
    if (!channelId) {
      const counter =
        data.channels.find((c) => c.slug === 'pos') ??
        data.channels.find((c) => c.slug === 'manual') ??
        data.channels[0];
      setChannelId(counter?.id ?? '');
    }
  }, [data, branchId, channelId, workingBranchId]);

  // The till's own currency, from the shop context rather than the session, so
  // the prices on screen come from the same response as the prices themselves.
  const currency = data?.currencyCode ?? business?.currencyCode ?? 'USD';

  /**
   * Searching asks the server, which searches the whole catalogue by name,
   * SKU and barcode and says whether each item can be sold here. The list
   * loaded with the till is only its first page — a catalogue of 1,190 items
   * filtered in the browser could not find most of them.
   */
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 150);
    return () => clearTimeout(t);
  }, [search]);
  const searching = term.length >= 2;
  const found = useQuery({
    queryKey: ['shop-items', business?.businessId, term, branchId],
    queryFn: () =>
      api.get<{ data: TillItem[] }>(
        path(`/shop/items${qs({ search: term, ...(branchId ? { branchId } : {}), limit: 30 })}`),
      ),
    enabled: Boolean(business) && searching,
    placeholderData: (previous) => previous,
  });

  const visible = useMemo(() => {
    if (!data) return [];
    const typed = search.trim().toLowerCase();
    if (!typed) return data.items;
    // The first letter narrows what is loaded; from the second, the server answers.
    return data.items.filter(
      (item) =>
        item.name.toLowerCase().includes(typed) || (item.sku ?? '').toLowerCase().includes(typed),
    );
  }, [data, search]);

  const unitPrice = (pharmacy ? '' : priceOverride.trim()) || selected?.unitPrice || '';
  const lineTotal =
    unitPrice && Number.isFinite(Number(unitPrice))
      ? (Number(unitPrice) * quantity).toFixed(2)
      : null;

  /**
   * Stage a line.
   *
   * A repeat of the same product at the same price adds to its row rather
   * than opening another — the same rule the server applies, mirrored here so
   * the screen shows what will actually be recorded. At a different price it
   * stays separate, because a discounted unit and a full-price one are
   * different facts.
   */
  const stage = (item: TillPick, qty: number, price: string): void => {
    setBasket((rows) => {
      const at = rows.findIndex((r) => r.productId === item.id && r.unitPrice === price);
      if (at === -1) {
        return [...rows, { productId: item.id, name: item.name, quantity: qty, unitPrice: price }];
      }
      const next = [...rows];
      next[at] = { ...next[at]!, quantity: next[at]!.quantity + qty };
      return next;
    });
  };

  const addSelectedToBasket = (): void => {
    if (!selected || !unitPrice) return;
    stage(selected, quantity, unitPrice);
    setSelected(null);
    setQuantity(1);
    setPriceOverride('');
    setSearch('');
    scanField.current?.focus();
  };

  /**
   * A scan.
   *
   * Matched against this tenant's own loaded catalogue for display; the server
   * re-resolves what it is given and remains the authority. An unknown code
   * says so rather than silently doing nothing, which is the difference
   * between a scanner people trust and one they stop using.
   */
  const submitScan = async (): Promise<void> => {
    const code = scan.trim().toUpperCase();
    if (code === '') return;

    // The server knows the whole catalogue and a pharmacy pack's own barcode;
    // the loaded list is only the fallback when it cannot be reached.
    const answer = await api
      .get<{ data: TillItem[] }>(path(`/shop/items${qs({ barcode: code, ...(branchId ? { branchId } : {}) })}`))
      .then((r) => (Array.isArray(r?.data) ? r.data : null))
      .catch(() => null);
    const hit = answer?.[0];
    if (hit && !hit.sellable) {
      setError(hit.reason ?? `${hit.name} cannot be sold here.`);
      setScan('');
      return;
    }
    const loaded = data?.items.find((i) => (i.barcode ?? '').toUpperCase() === code);
    const item: TillPick | undefined = hit ? fromSearch(hit) : answer ? undefined : loaded;
    if (!item) {
      setError(`No product in your catalogue has the barcode ${code}.`);
      setScan('');
      return;
    }
    if (!item.unitPrice) {
      setError(`${item.name} has no price on file. Add one, or ring it up by hand.`);
      setScan('');
      return;
    }

    setError(null);
    stage(item, 1, item.unitPrice);
    setScan('');
    scanField.current?.focus();
  };

  const basketTotal = basket.reduce((sum, r) => sum + Number(r.unitPrice) * r.quantity, 0);

  const record = useMutation({
    mutationFn: (): Promise<ShopSaleResult> =>
      api.post<ShopSaleResult>(path('/shop/sales'), {
        /**
         * One shape or the other, never both.
         *
         * With an empty basket this is the original single-line request,
         * untouched. With a basket it sends every staged row plus whatever is
         * still selected, so the item on screen is never silently dropped from
         * the sale the cashier just took money for.
         */
        ...(basket.length > 0
          ? {
              lines: [
                ...basket.map((row) => ({
                  productId: row.productId,
                  quantity: row.quantity,
                  unitPrice: row.unitPrice,
                })),
                ...(selected && unitPrice
                  ? [{ productId: selected.id, quantity, unitPrice }]
                  : []),
              ],
            }
          : {
              line: {
                productId: selected?.id ?? null,
                quantity,
                ...(!pharmacy && priceOverride.trim() ? { unitPrice: priceOverride.trim() } : {}),
                ...(selected ? {} : { description: search.trim() || 'Counter sale' }),
              },
            }),
        // Survives a retry on a bad connection without charging twice.
        idempotencyKey: checkoutKey.current,
        ...(customerId ? { customerId } : {}),
        ...(!customerId && newCustomerName.trim()
          ? {
              newCustomer: {
                name: newCustomerName.trim(),
                ...(newCustomerPhone.trim() ? { phone: newCustomerPhone.trim() } : {}),
              },
            }
          : {}),
        /**
         * Where the sale happened. Required, never inferred.
         *
         * A sale with no location is a sale nobody can attribute, so the till
         * states one — prefilled from the shared selection, correctable here.
         * The backend still refuses a location this login may not write to.
         */
        branchId,
        staffId: staffId || null,
        channelId: channelId || null,
        paymentMethod,
        paymentStatus,
        ...(confirmPayment ? { paymentConfirmed } : {}),
        ...(referencePayment ? { paymentReference: paymentReference.trim() } : {}),
      }),
    onSuccess: (sale) => {
      setResult(sale);
      setError(null);
      // Reset the line but keep the attribution — the next customer is usually
      // served by the same person at the same counter.
      setSelected(null);
      setQuantity(1);
      setPriceOverride('');
      setSearch('');
      setBasket([]);
      setScan('');
      // A fresh key: the next customer is a different checkout, and reusing
      // this one would make their sale look like a retry of the last.
      checkoutKey.current = newCheckoutKey();
      setCustomerId('');
      setNewCustomerName('');
      setNewCustomerPhone('');
      setPaymentConfirmed(false);
      setPaymentReference('');
      setCapturingCustomer(false);
      // Everything a till sale changes. `sales` and `catalogue` matter as much
      // as the rest: a sale recorded here is an ordinary sale and an ordinary
      // depletion, so the history that lists it and the catalogue that shows
      // its on-hand are stale the moment this returns. Leaving them out meant
      // the counter recorded a sale, opened Sales to check it, and did not see
      // it — which reads as "the sale did not save" and invites a re-entry.
      void queryClient.invalidateQueries({ queryKey: ['shop-context'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['catalogue'] });
    },
    onError: (caught) => {
      setResult(null);
      setError(caught instanceof ApiError ? caught.message : 'Could not record the sale.');
    },
  });

  const noun = data?.catalogueNoun ?? { singular: 'Product', plural: 'Products' };
  const namedCustomer = Boolean(customerId || newCustomerName.trim());
  const pharmacyBlock = pharmacy
    ? !selected && basket.length === 0
      ? 'Pick or scan an item: a pharmacy does not record a typed amount.'
      : paymentMethod === 'credit' && paymentStatus === 'paid'
        ? 'Credit is not a payment: set the status to Owing, for a named customer.'
        : (paymentStatus !== 'paid' || paymentMethod === 'credit') && !namedCustomer
          ? 'Goods leave unpaid only as a named customer’s debt. Add the customer, or take payment.'
          : referencePayment && !transferReferenceReady(paymentReference)
            ? 'Enter the reference on the bank alert. One transfer pays once.'
            : confirmPayment && !paymentConfirmed
              ? 'Tick that you have seen the slip or the bank alert — or set the status to Owing, for a named customer.'
              : null
    : null;
  const canRecord =
    (Boolean(lineTotal) || basket.length > 0) && !record.isPending && Boolean(branchId) && !pharmacyBlock;

  /**
   * Submit exactly once per gesture.
   *
   * `disabled={!canRecord}` is not enough on its own. `isPending` is state, and
   * the two clicks of a double-tap arrive before React has re-rendered the
   * button, so both pass the check and two identical sales are written — each
   * with its own stock depletion. On a counter that is a customer charged
   * twice and inventory short by one, found days later during a stock count.
   *
   * A ref is read and written synchronously in the same tick, which is what
   * closes the window. It is cleared when the mutation settles, so a genuine
   * second sale of the same item — the next customer buying the same thing —
   * is still one click away.
   */
  const inFlight = useRef(false);

  const submitSale = (): void => {
    if (inFlight.current || !canRecord) return;
    inFlight.current = true;
    record.mutate(undefined, { onSettled: () => { inFlight.current = false; } });
  };

  return (
    <>
      <PageHeader
        title="Shop"
        description="Record a sale as it happens"
        action={
          <Link to="/catalogue">
            <Button variant="secondary" icon={<PackagePlus className="size-4" />}>
              Manage {noun.plural.toLowerCase()}
            </Button>
          </Link>
        }
      />

      {welcome && (
        <Card className="mb-4 border-brand-200 bg-brand-50/60">
          <div className="flex items-start gap-3">
            <ShoppingBag className="mt-0.5 size-5 shrink-0 text-brand-600" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900">Your business is ready.</p>
              <p className="mt-1 text-sm text-slate-600">
                Add your first {noun.singular.toLowerCase()}, then record a sale here. Everything
                you record flows straight into your reports.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/catalogue?new=1">
                  <Button size="sm" icon={<PackagePlus className="size-4" />}>
                    Add your first {noun.singular.toLowerCase()}
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    params.delete('welcome');
                    setParams(params, { replace: true });
                  }}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {isPending ? (
        <Spinner label="Opening the shop" />
      ) : loadError ? (
        <ErrorState message="Could not open the shop." onRetry={() => refetch()} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_22rem]">
          {/* ── Catalogue picker ──────────────────────────────────────────── */}
          <Card>
            <Input
              label={`Find a ${noun.singular.toLowerCase()}`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, SKU or barcode, or just type what was sold"
            />

            {searching ? (
              <SearchHits
                hits={found.data?.data ?? null}
                loading={found.isPending}
                term={term}
                currency={currency}
                selectedId={selected?.id ?? null}
                onPick={(hit) => {
                  setSelected(fromSearch(hit));
                  setPriceOverride('');
                }}
              />
            ) : data.items.length === 0 ? (
              <div className="mt-4">
                <EmptyState
                  icon={<PackagePlus className="size-6" />}
                  title={`No ${noun.plural.toLowerCase()} yet`}
                  description={`Add your first ${noun.singular.toLowerCase()}, or record a one-off sale using the amount box.`}
                  action={
                    <Link to="/catalogue?new=1">
                      <Button icon={<PackagePlus className="size-4" />}>
                        Add {noun.singular.toLowerCase()}
                      </Button>
                    </Link>
                  }
                />
              </div>
            ) : (
              <ul className="mt-4 grid max-h-[26rem] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
                {visible.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelected(item);
                        setPriceOverride('');
                      }}
                      className={cn(
                        'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                        selected?.id === item.id
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-slate-200 hover:bg-slate-50',
                      )}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium text-slate-900">
                          {item.name}
                        </span>
                        <span className="shrink-0 text-sm tabular text-slate-700">
                          {item.unitPrice ? money(item.unitPrice, currency) : '—'}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {item.kind === 'service' ? (
                          <Badge tone="info">Service</Badge>
                        ) : item.onHand === null ? (
                          <span className="text-xs text-slate-400">Not tracked</span>
                        ) : (
                          <Badge tone={item.onHand <= 0 ? 'danger' : item.isLow ? 'warning' : 'neutral'}>
                            {item.onHand} in stock
                          </Badge>
                        )}
                        {item.categoryName && (
                          <span className="truncate text-xs text-slate-500">
                            {item.categoryName}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* ── The sale ──────────────────────────────────────────────────── */}
          <div className="space-y-4">
            <Card>
              <h2 className="text-base font-semibold text-slate-900">This sale</h2>

              <p className="mt-1 text-sm text-slate-600">
                {selected ? (
                  selected.name
                ) : (
                  <span className="italic text-slate-500">
                    {pharmacy ? 'Nothing selected — pick or scan an item' : 'Nothing selected — enter an amount for a one-off sale'}
                  </span>
                )}
              </p>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <Input
                  label="Quantity"
                  type="number"
                  min={1}
                  value={String(quantity)}
                  onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
                />
                <Input
                  label="Unit price"
                  inputMode="decimal"
                  value={(pharmacy ? '' : priceOverride) || selected?.unitPrice || ''}
                  readOnly={pharmacy}
                  onChange={(event) => setPriceOverride(event.target.value)}
                  hint={pharmacy ? 'The price on file' : selected?.unitPrice ? 'Override to negotiate' : undefined}
                />
              </div>

              {/* Scan straight into the basket. A wedge scanner types the
                  digits and presses Enter, which is exactly this field. */}
              <div className="mt-3 flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <label className="text-xs text-slate-500" htmlFor="till-scan">
                    Scan a barcode
                  </label>
                  <input
                    id="till-scan"
                    ref={scanField}
                    type="text"
                    value={scan}
                    autoComplete="off"
                    placeholder="Scan or type"
                    onChange={(event) => setScan(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void submitScan();
                      }
                    }}
                    className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-2 text-base tabular"
                  />
                </div>
                {selected && unitPrice && (
                  <Button variant="secondary" onClick={addSelectedToBasket}>
                    Add to basket
                  </Button>
                )}
              </div>

              {basket.length > 0 && (
                <ul className="mt-3 space-y-1.5" aria-label="Basket">
                  {basket.map((row) => (
                    <li
                      key={`${row.productId}-${row.unitPrice}`}
                      className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <span className="min-w-0 flex-1 truncate text-slate-900">
                        {row.quantity} × {row.name}
                      </span>
                      <span className="tabular text-slate-700">
                        {money((Number(row.unitPrice) * row.quantity).toFixed(2), currency)}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${row.name}`}
                        onClick={() =>
                          setBasket((rows) =>
                            rows.filter(
                              (r) =>
                                !(r.productId === row.productId && r.unitPrice === row.unitPrice),
                            ),
                          )
                        }
                        className="shrink-0 text-xs text-rose-700"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex items-baseline justify-between rounded-lg bg-slate-50 px-3 py-2.5">
                <span className="text-sm text-slate-600">
                  {basket.length > 0 ? `Basket (${basket.length} item${basket.length === 1 ? '' : 's'})` : 'Total'}
                </span>
                <span className="text-lg font-semibold tabular text-slate-900">
                  {basket.length > 0
                    ? money(
                        (basketTotal + (lineTotal ? Number(lineTotal) : 0)).toFixed(2),
                        currency,
                      )
                    : lineTotal
                      ? money(lineTotal, currency)
                      : '—'}
                </span>
              </div>

              {/* Stock warning shown BEFORE the sale, so it is a decision and
                  not a surprise. The sale is still allowed. */}
              {selected?.onHand !== null &&
                selected !== null &&
                selected.onHand - quantity < 0 && (
                  <p className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                    Only {selected.onHand} in stock. This sale will take you to{' '}
                    {selected.onHand - quantity}. It will still be recorded.
                  </p>
                )}

              <div className="mt-3 grid grid-cols-2 gap-3">
                <Select
                  label="Payment"
                  options={PAYMENT_METHODS.map((value) => ({
                    value,
                    label: PAYMENT_METHOD_LABELS[value],
                  }))}
                  value={paymentMethod}
                  onChange={(event) => {
                    setPaymentMethod(event.target.value);
                    setPaymentConfirmed(false);
                  }}
                />
                <Select
                  label="Status"
                  options={[
                    { value: 'paid', label: 'Paid' },
                    { value: 'unpaid', label: 'Owing' },
                  ]}
                  value={paymentStatus}
                  onChange={(event) => setPaymentStatus(event.target.value as 'paid' | 'unpaid')}
                />
              </div>

              {confirmPayment && (
                <div className="mt-3 space-y-2">
                  {referencePayment && (
                    <Input
                      label="Transfer reference"
                      placeholder="Session ID or reference on the bank alert"
                      hint={TRANSFER_REFERENCE_HINT}
                      value={paymentReference}
                      onChange={(event) => setPaymentReference(event.target.value)}
                    />
                  )}
                  <label className="flex items-start gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={paymentConfirmed}
                      onChange={(event) => setPaymentConfirmed(event.target.checked)}
                    />
                    I have seen the approval (terminal slip / bank alert)
                  </label>
                </div>
              )}

              {pharmacyBlock && (selected || basket.length > 0 || paymentStatus !== 'paid' || paymentMethod === 'credit') && (
                <p className="mt-3 text-xs text-amber-700">{pharmacyBlock}</p>
              )}

              <Button
                className="mt-4 w-full"
                disabled={!canRecord}
                onClick={submitSale}
                icon={<Check className="size-4" />}
              >
                {record.isPending
                  ? 'Recording…'
                  : basket.length > 0
                    ? 'Take payment'
                    : 'Record sale'}
              </Button>

              {error && (
                <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  {error}
                </p>
              )}
            </Card>

            {/* ── Who and where. Defaulted, collapsed, rarely touched. ─────── */}
            <Card>
              <h2 className="text-sm font-semibold text-slate-900">Customer</h2>

              {!capturingCustomer ? (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-slate-500">
                    Walk-in by default — no details needed.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<UserPlus className="size-4" />}
                    onClick={() => setCapturingCustomer(true)}
                  >
                    Add customer details
                  </Button>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  <Input
                    label="Name"
                    value={newCustomerName}
                    onChange={(event) => setNewCustomerName(event.target.value)}
                  />
                  <Input
                    label="Phone (optional)"
                    value={newCustomerPhone}
                    onChange={(event) => setNewCustomerPhone(event.target.value)}
                    hint="Matches an existing customer if they have bought before"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCapturingCustomer(false);
                      setNewCustomerName('');
                      setNewCustomerPhone('');
                    }}
                  >
                    Cancel — record as walk-in
                  </Button>
                </div>
              )}

              <div className="mt-4 space-y-3 border-t border-slate-200 pt-3">
                {(data.branches.length > 1 || !branchId) && (
                  <Select
                    label="Location"
                    required
                    placeholder="Choose a location"
                    options={data.branches.map((b) => ({ value: b.id, label: b.name }))}
                    value={branchId}
                    onChange={(event) => setBranchId(event.target.value)}
                    {...(branchId ? {} : { error: 'Choose where this sale happened.' })}
                  />
                )}
                {data.staff.length > 0 && (
                  <Select
                    label="Sold by"
                    options={[
                      { value: '', label: 'Not recorded' },
                      ...data.staff.map((s) => ({ value: s.id, label: s.fullName })),
                    ]}
                    value={staffId}
                    onChange={(event) => setStaffId(event.target.value)}
                  />
                )}
                <Select
                  label="Channel"
                  options={data.channels.map((c) => ({ value: c.id, label: c.name }))}
                  value={channelId}
                  onChange={(event) => setChannelId(event.target.value)}
                />
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── Confirmation, with any warnings the server returned ──────────── */}
      {result && (
        <Card className="mt-4 border-emerald-200 bg-emerald-50/60">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-medium text-emerald-900">
                <Check className="size-4" aria-hidden />
                {/* The sale's own currency: a branch abroad stamps its rows
                    with its own, and the receipt has to say what was actually
                    recorded rather than what the price list was shown in. */}
                {result.reference} recorded — {money(result.amount, result.currencyCode)}
              </p>
              <p className="mt-1 text-sm text-emerald-800">
                {result.customerName ?? 'Walk-in customer'}
                {result.remainingStock !== null && ` · ${result.remainingStock} left in stock`}
              </p>

              {result.warnings.map((warning) => (
                <p
                  key={warning.code}
                  className="mt-2 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {warning.message}
                </p>
              ))}
            </div>

            <Link to="/reports/today">
              <Button variant="secondary" size="sm" icon={<ArrowRight className="size-4" />}>
                See it in today&rsquo;s report
              </Button>
            </Link>
          </div>
        </Card>
      )}
    </>
  );
}

/**
 * What the server found. Every match is listed with whether it can be sold
 * here; one that cannot is shown, labelled and greyed, never hidden — the
 * cashier learns it is in the catalogue and why it cannot be rung up.
 */
function SearchHits({
  hits,
  loading,
  term,
  currency,
  selectedId,
  onPick,
}: {
  hits: TillItem[] | null;
  loading: boolean;
  term: string;
  currency: string;
  selectedId: string | null;
  onPick: (hit: TillItem) => void;
}): ReactNode {
  if (loading && !hits) return <p className="mt-4 text-sm text-slate-500">Searching…</p>;
  if (!hits?.length) {
    return <p className="mt-4 text-sm text-slate-500">Nothing in the catalogue matches “{term}”.</p>;
  }
  return (
    <ul aria-label="Search results" className="mt-4 grid max-h-[26rem] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
      {hits.map((hit) => (
        <li key={hit.id}>
          <button
            type="button"
            disabled={!hit.sellable}
            title={hit.reason ?? undefined}
            onClick={() => onPick(hit)}
            className={cn(
              'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
              !hit.sellable
                ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-80'
                : selectedId === hit.id
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-slate-200 hover:bg-slate-50',
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-medium text-slate-900">{hit.name}</span>
              <span className="shrink-0 text-sm tabular text-slate-700">{hit.price ? money(hit.price, currency) : '—'}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge tone={tillStateTone(hit.state)}>{tillStateLabel(hit.state)}</Badge>
              {hit.saleableOnHand !== null && <span className="text-xs text-slate-500">{hit.saleableOnHand} in stock</span>}
              {hit.sku && <span className="truncate text-xs text-slate-500">SKU {hit.sku}</span>}
            </div>
            {!hit.sellable && hit.reason && <p className="mt-1 text-xs text-amber-700">{hit.reason}</p>}
          </button>
        </li>
      ))}
    </ul>
  );
}
